/**
 * Auto-filled WorkWise fields. Each field has a suggested value and a dependency
 * key: the suggestion is re-applied only when its own inputs change AND the
 * field is still "managed" (empty, or still equal to the previous suggestion).
 * A value the user has edited is therefore never overwritten, and a field the
 * user clears is not refilled while they type.
 *
 * VM names come from GET /workwise/vm-names/ (the next free name per role);
 * until it answers, app/db fall back to the convention name with sequence 01.
 * Sizes are not suggested: a dedicated DB / gateway simply uses the step-2 size
 * (R3-G). The network is not a field either (revision 3): it is derived and
 * shown read-only. Revision 8 removed the gateway host name (and its default).
 *
 * Node-safe: relative imports only, no import.meta.
 */
import { osIdForAdminOsType } from '../../config/workwise'
import type { WorkwiseEnvironment } from '../../types/workwise'
import { appVmName, dbConnectionRef, dbVmName, isValidCustomerNumber, slugify } from './naming'

export type AutoFieldPath =
  | 'customer.slug'
  | 'appServer.vmName'
  | 'appServer.os'
  | 'dbServer.vmName'
  | 'gatewayServer.vmName'
  | 'app.tenantName'
  | 'app.dbConnectionRef'
  | 'app.version'
  | 'tags.owner'

export interface AutoInputs {
  customerName: string
  environment: WorkwiseEnvironment
  customerNumber: string
  /** `next` names from GET /workwise/vm-names/ (undefined while loading). */
  appNext?: string | null
  dbNext?: string | null
  gatewayNext?: string | null
  /** os_type of the step-2 admin size (windows / linux). */
  adminOsType?: string | null
  /** The default entry of GET /workwise/versions/. */
  defaultVersion?: string
  owner?: string
}

export interface AutoSuggestion {
  value: string
  /** Changes only when an input of this field changes. */
  key: string
}

/** Every auto-filled field, in the order the suggestions are applied. */
export const AUTO_FIELD_PATHS: AutoFieldPath[] = [
  'customer.slug',
  'appServer.vmName',
  'appServer.os',
  'dbServer.vmName',
  'gatewayServer.vmName',
  'app.tenantName',
  'app.dbConnectionRef',
  'app.version',
  'tags.owner',
]

export function suggestFields(input: AutoInputs): Record<AutoFieldPath, AutoSuggestion> {
  const { environment, customerNumber } = input
  const numberOk = isValidCustomerNumber(customerNumber)
  const nameKey = `${environment}|${customerNumber}`
  const appName = input.appNext ?? (numberOk ? appVmName(environment, customerNumber) : '')
  const dbName = input.dbNext ?? (numberOk ? dbVmName(environment, customerNumber) : '')
  return {
    'customer.slug': { value: slugify(input.customerName), key: input.customerName },
    'appServer.vmName': { value: numberOk ? appName : '', key: `${nameKey}|${input.appNext ?? ''}` },
    'appServer.os': { value: osIdForAdminOsType(input.adminOsType), key: input.adminOsType ?? '' },
    'dbServer.vmName': { value: numberOk ? dbName : '', key: `${nameKey}|${input.dbNext ?? ''}` },
    'gatewayServer.vmName': { value: input.gatewayNext ?? '', key: `${environment}|${input.gatewayNext ?? ''}` },
    'app.tenantName': { value: input.customerName.trim(), key: input.customerName },
    'app.dbConnectionRef': { value: numberOk ? dbConnectionRef(customerNumber, environment) : '', key: nameKey },
    'app.version': { value: input.defaultVersion ?? '', key: input.defaultVersion ?? '' },
    'tags.owner': { value: input.owner ?? '', key: input.owner ?? '' },
  }
}

/**
 * Decides whether a field should take its new suggestion.
 * `previous` is the suggestion applied last time (or undefined on first run).
 */
export function shouldApplySuggestion(
  current: string,
  next: AutoSuggestion,
  previous: AutoSuggestion | undefined,
): boolean {
  if (previous && previous.key === next.key) return false // inputs unchanged
  if (current === next.value) return false
  return current === '' || current === (previous?.value ?? next.value)
}
